// Orchestrates the extension promo video (Admin::Promo::Video::ExtensionPromoController).
// Played once for screen capture (1920×1080):
//   Scene 1 (landing hero) reveals → holds → Scene 2 (feature showcase) slides in from the
//   right with a gentle scale settle and its carousel cycles every tool → Scene 3 (brand outro)
//   slides in, the lockup reveals, and the video rests there.
//
// Scenes 1–2 are one timeline; Scene 3 waits on the carousel's `feature-showcase:complete`,
// which only a non-looping showcase emits (`loop: false`) — its real advance depends on each
// slide's choreography, so any predicted hold here would cut the last tool off.
//
// The showcase scene is parked off the right of the window until Scene 2 begins; that keeps the
// FeatureShowcaseComponent's IntersectionObserver-driven carousel from starting early.

import { Controller } from '@hotwired/stimulus';
import { gsap } from 'gsap';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Mirrors landing_scroll_controller's reveal so Scene 1 matches the live hero exactly.
const REVEAL_FROM = { opacity: 0, y: 28, filter: 'blur(10px)' };
const REVEAL_TO = { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power3.out', clearProps: 'transform,filter' };
const REVEAL_STAGGER = 0.09;

const INTRO_HOLD = 2.6; // seconds the fully-revealed hero dwells before the showcase slides in
const SWIPE_DURATION = 0.8;
const SWIPE_EASE = 'power3.inOut';

export default class extends Controller {
  static targets = ['intro', 'showcase', 'outro'];

  connect() {
    if (this.__reducedMotion) {
      this.__revealStatic();
      return;
    }

    gsap.set([this.showcaseTarget, this.outroTarget], { xPercent: 120, opacity: 1 });
    gsap.set(this.showcaseTarget, { scale: 1.04, transformOrigin: '50% 50%' });
    gsap.set(this.__introAnimateEls(), REVEAL_FROM);

    this.__onShowcaseComplete = () => this.__outroTl?.play();
    this.element.addEventListener('feature-showcase:complete', this.__onShowcaseComplete);

    this.__buildTimeline();
    this.__buildOutroTimeline();
  }

  disconnect() {
    if (this.__onShowcaseComplete) this.element.removeEventListener('feature-showcase:complete', this.__onShowcaseComplete);
    this.__tl?.kill();
    this.__outroTl?.kill();
  }

  // private

  get __reducedMotion() {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  __introAnimateEls() {
    return this.introTarget.querySelectorAll('[data-landing-scroll-target="animate"]');
  }

  // Reduced motion: show the hero statically; leave later scenes parked off-window.
  __revealStatic() {
    gsap.set(this.__introAnimateEls(), { opacity: 1, y: 0, filter: 'none' });
    gsap.set([this.showcaseTarget, this.outroTarget], { xPercent: 120, opacity: 1 });
  }

  __buildTimeline() {
    const tl = gsap.timeline();
    this.__tl = tl;

    // Scene 1 — hero reveal (fade + slide + blur, cascaded in DOM order).
    const introEls = this.__introAnimateEls();
    introEls.forEach((el, i) => tl.fromTo(el, REVEAL_FROM, REVEAL_TO, 0.2 + i * REVEAL_STAGGER));
    const introRevealEnd = 0.2 + (introEls.length - 1) * REVEAL_STAGGER + REVEAL_TO.duration;

    // Scene 2 — showcase slides in from the right with a tiny scale settle; the carousel auto-starts.
    const showcaseInAt = introRevealEnd + INTRO_HOLD;
    tl.to(this.showcaseTarget, { xPercent: 0, scale: 1, duration: SWIPE_DURATION, ease: SWIPE_EASE }, showcaseInAt);
  }

  // Scene 3 — outro slides in once every tool has played; held paused until the carousel says so.
  __buildOutroTimeline() {
    const tl = gsap.timeline({ paused: true });
    this.__outroTl = tl;

    tl.to(this.outroTarget, { xPercent: 0, duration: SWIPE_DURATION, ease: SWIPE_EASE }, 0);

    // Outro reveal — accent glow blooms, the logo pops in, then wordmark + tagline cascade.
    const outroStart = SWIPE_DURATION - 0.1;
    const glow = this.outroTarget.querySelector('[data-promo-outro="glow"]');
    if (glow) tl.fromTo(glow, { opacity: 0, scale: 0.55 }, { opacity: 1, scale: 1, duration: 0.9, ease: 'power2.out' }, outroStart);

    const logo = this.outroTarget.querySelector('[data-promo-outro="logo"]');
    if (logo)
      tl.fromTo(logo, { opacity: 0, scale: 0.7, filter: 'blur(6px)' }, { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.7, ease: 'back.out(1.6)' }, outroStart + 0.15);

    // Finish — the video rests on the brand lockup (no fade-out); the recording is cut here.
    const revealEls = this.outroTarget.querySelectorAll('[data-promo-outro="reveal"]');
    revealEls.forEach((el, i) => {
      const at = outroStart + 0.28 + i * 0.12;
      tl.fromTo(el, { opacity: 0, y: 24, filter: 'blur(8px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.65, ease: 'power3.out' }, at);

      // The tagline's hero-accent slab swipes in via a CSS animation that fires at page
      // load, long before the outro is visible — replay it as the tagline reveals.
      const slab = el.querySelector('.hta-label');
      if (slab)
        tl.call(
          () => {
            slab.style.animation = 'none';
            void slab.offsetWidth;
            slab.style.animation = '';
          },
          null,
          at,
        );
    });
  }
}
